/* React hooks */
import { useEffect, useRef, useState } from "react";
import { safeJwtDecode } from "../../../helper/jwtHelper";
/* Contexts */
import {
    checkRequiredFields,
    getErrorListFromValidation,
    removeErrorList,
    validateFormInput
} from "../../../helper/tools";
import { getFunerariumGeneralInfos } from "../../../api/pro/funerariumReq";
import {
    getEventInfos,
    getEventPlaceList,
    getEventSubList,
    getEventTypeList, updateEventInfos
} from "../../../api/pro/eventReq";
import useAuth from "../../../hooks/useAuth";
import { useParams } from "react-router-dom";
/* Components */
import InputFullAddress from "../../../components/common/input-layout/InputFullAddress";
import FormButtons from "../../../components/common/FormButtons";
import useDeceased from "../../../hooks/useDeceased";
import useData from "../../../hooks/useData";
import Icon from "../../../components/common/Icon";
import {getAgencyContactInfos, getAgencyMainInfos, getAgencySiteInfos} from "../../../api/admin/agencyReq";
import DateInput from "../../../components/common/input-layout/DateInput";


export default function FormAddEvent({ apiDeceasedInfos, deceasedInfos, eventKey, eventId, setShowModal, apiEventList }) {
    const { id } = useParams();
    const { auth } = useAuth();
    const token = auth.token;
    const decodedToken = safeJwtDecode(token);
    const agencyId = decodedToken?.agency;
    const today = new Date().toISOString().slice(0, 10);

    const [eventTypeList, setEventTypeList] = useState();
    const [selectedEventSubList, setSelectedEventSubList] = useState();
    const [selectedPlaceList, setSelectedPlaceList] = useState();
    const [selectedEventType, setSelectedEventType] = useState();
    const [selectedEventName, setSelectedEventName] = useState();
    const [selectedPlace, setSelectedPlace] = useState();
    const [values, setValues] = useState();
    const [published, setPublished] = useState(1);
    const [eventInfos, setEventInfos] = useState();
    const [funerariumData, setFunerariumData] = useState();
    const [commonCremationSubListPlaces, setCommonCremationSubListPlaces] = useState();
    const [departCremationSubListPlaces, setDepartCremationSubListPlaces] = useState();
    const [ flowerLocations, setFlowerLocations] = useState();
    const [agencyContactInfos, setAgencyContactInfos] = useState();
    const [agencyMainInfos, setAgencyMainInfos] = useState();

    const eventTypeRef = useRef();
    const eventNameRef = useRef();
    // Indique que les infos/horaires de visite actuellement affichés proviennent d'un
    // pré-remplissage automatique depuis le funérarium de l'agence (cf. effet ci-dessous) : sert
    // à les effacer si le lieu change ensuite vers autre chose qu'un funérarium de l'agence, sans
    // jamais effacer des infos saisies manuellement ou chargées depuis un événement existant.
    const funerariumAutoFilledRef = useRef(false);
    const placeRef = useRef();
    const hasLoadedEventInfosRef = useRef(false);

    const { selectedCity, setSelectedCity } = useData({});
    const [deceasedValues, setDeceasedValues] = useState(deceasedInfos?.actualplace);

    // Helpers pour les conditions d'adresse
    const getAddressConditions = (eventType, eventName, place) => ({
        isDeceasedAddress: ((eventType === "Recueillement" || eventType === "Rassemblement" || eventType === "VinHonneur") && place == 6) ||
            (eventType === "Cremation" && eventName == 7 && place == 7),

        isFunerariumAddress: (eventType !== "Ceremonie" && eventType !== "Cremation" && place == 10) ||
            (eventType === "Cremation" && place == 9),

        shouldShowAddressForm: (eventType && eventName && place) ||
            ((eventType === "Ceremonie" && place != null) ||
                (eventType === "Obseques" && place != null))
    });

    useEffect(() => {
        if (selectedCity?.city_zip) {
            setValues(prev => ({
                ...prev,
                actualPlace: {
                    ...prev?.actualPlace,
                    zipcode: selectedCity?.zip,
                    city: selectedCity?.name
                },
            }))
        }
    }, [selectedCity]);

    useEffect(() => {
        if (selectedPlaceList) {
            setCommonCremationSubListPlaces(selectedPlaceList.filter(place => place.id !== 7 && place.id !== 8 && place.id !== 9));
            setDepartCremationSubListPlaces(selectedPlaceList.filter(place => place.id === 7 || place.id === 8 || place.id === 9));
        }
    }, [selectedPlaceList]);


    useEffect(() => {
        if (!values?.actualPlace) {
            setValues(prev => ({
                ...prev,
                actualPlace: {
                    country: "FR",
                    address: "",
                    addressComp: "",
                    zipcode: "",
                    city: ""
                }
            }))
        }
    }, [values?.actualPlace]);

    const agencyInfos = async() => {
        const response = await getAgencySiteInfos(token, agencyId)
        setFlowerLocations(response)
    }

    useEffect(() => {
        if (!flowerLocations) {
            agencyInfos()
        }
    }, [flowerLocations])

    const normalizeTimeFormat = (timeValue) => {
        if (!timeValue) return "";

        const timeStr = String(timeValue);

        if (!timeStr || timeStr === "null" || timeStr === "undefined") return "";

        if (/^\d{2}:\d{2}$/.test(timeStr)) {
            return timeStr;
        }

        const timeMatch = timeStr.match(/^(\d{1,2}):(\d{1,2})$/);
        if (timeMatch) {
            const hours = timeMatch[1].padStart(2, '0');
            const minutes = timeMatch[2].padStart(2, '0');
            return `${hours}:${minutes}`;
        }

        const numbersOnly = timeStr.replace(/\D/g, "");
        if (numbersOnly.length === 3 || numbersOnly.length === 4) {
            const hours = numbersOnly.slice(0, -2).padStart(2, '0');
            const minutes = numbersOnly.slice(-2);
            return `${hours}:${minutes}`;
        }

        return timeStr;
    };

    const apiEventInfos = async () => {
        const response = await getEventInfos(token, eventId, eventKey)

        const normalizedMorningStart = normalizeTimeFormat(response?.morningStart);
        const normalizedAfternoonStart = normalizeTimeFormat(response?.afternoonStart);

        const normalizedResponse = {
            ...response,
            time: normalizeTimeFormat(response?.time),
            morningStart: normalizedMorningStart,
            morningEnd: normalizeTimeFormat(response?.morningEnd),
            afternoonStart: normalizedAfternoonStart,
            afternoonEnd: normalizeTimeFormat(response?.afternoonEnd),
            schedulePeriod: (normalizedMorningStart && normalizedAfternoonStart) ? "both"
                : normalizedMorningStart ? "morning"
                    : normalizedAfternoonStart ? "afternoon"
                        : "both",
        };

        setEventInfos(normalizedResponse)
        setValues(normalizedResponse)

        // Ne pré-remplir le type/nom/lieu qu'au tout premier chargement : si cet effet se
        // redéclenche plus tard (ex: token rafraîchi en arrière-plan pendant l'édition), il ne
        // doit pas écraser silencieusement les sélections déjà faites par l'utilisateur.
        if (!hasLoadedEventInfosRef.current) {
            setSelectedEventType(eventKey)
            setSelectedEventName(response?.eventType)
            setSelectedPlace(response?.placeKey)
            hasLoadedEventInfosRef.current = true
        }
    }

    const apiFunerariumInfos = async () => {
        const response = await getFunerariumGeneralInfos(token, agencyId)
        const data = response?.data || response;
        const fData = data?.addressFunerarium || data;

        if (fData) {
            setFunerariumData(fData);
        }
    }

    const apiAgencyContactInfos = async () => {
        const response = await getAgencyContactInfos(token, agencyId)
        setAgencyContactInfos(response)
    }

    const apiAgencyMainInfos = async () => {
        const response = await getAgencyMainInfos(token, agencyId)
        setAgencyMainInfos(response)
    }

    const apiEventTypeList = async () => {
        const response = await getEventTypeList(token)
        setEventTypeList(response)
    }

    useEffect(() => {
        apiEventTypeList()
    }, [token]);

    const apiEventSubList = async () => {
        const response = await getEventSubList(token, selectedEventType)
        setSelectedEventSubList(response)
    }

    const apiPlaceList = async () => {
        const response = await getEventPlaceList(token, selectedEventType)
        setSelectedPlaceList(response)
    }
    const handleRemoveModal = (e) => {
        setShowModal(false)
    }

    useEffect(() => {
        if (selectedEventType) {
            apiEventSubList()
        }

    }, [selectedEventType]);

    // Le placeholder "Préciser" est disabled : le navigateur affiche donc automatiquement le
    // premier <option> réel comme valeur par défaut du select, sans jamais déclencher onChange
    // (qui ne se déclenche que sur un choix explicite de l'utilisateur). Sans cette resynchro,
    // selectedEventName reste null tant que l'utilisateur ne change pas puis revient sur ce
    // premier choix, alors qu'il est déjà affiché comme sélectionné à l'écran.
    useEffect(() => {
        // En édition, ne pas interférer tant que le chargement initial (apiEventInfos) n'a pas
        // appliqué la vraie valeur enregistrée ; ensuite, comme en création, resynchroniser.
        if (selectedEventName == null && (eventId == 0 || hasLoadedEventInfosRef.current)) {
            const firstOption = selectedEventSubList?.find(event => event != null);
            if (firstOption) {
                setSelectedEventName(String(firstOption.id));
            }
        }
    }, [selectedEventSubList, eventId]);

    useEffect(() => {
        if (selectedEventName != null || eventId == 0 || selectedEventType === "Ceremonie" || selectedEventType === "Obseques") {
            apiPlaceList()
        }
    }, [selectedEventName, eventId]);



    const onChangeMainEvent = (e) => {
        setSelectedEventType(eventTypeRef?.current?.value)
        // Toujours réinitialiser (pas seulement en création) : en édition, si le nouveau nom
        // d'événement choisi a la même valeur que l'ancien (ex: "1" pour Visite déjà présent),
        // React ne détecte aucun changement d'état et l'effet qui recharge les lieux ne se
        // redéclenche pas, laissant "Type de lieu" vide.
        setSelectedEventName(null)
        setSelectedPlace(null)
        validateFormInput(e)
    }

    // Si eventId change (édition d'un autre événement), autoriser à nouveau le préremplissage
    // initial du type/nom/lieu ; un simple rafraîchissement de token ne doit pas le déclencher.
    useEffect(() => {
        hasLoadedEventInfosRef.current = false
    }, [eventId]);

    useEffect(() => {
        if (eventId != 0) {
            apiEventInfos()
        }
        if (token && agencyId) {
            apiFunerariumInfos()
            apiAgencyContactInfos()
            apiAgencyMainInfos()
        }
    }, [eventId, token, agencyId]);

    useEffect(() => {
        if (eventNameRef?.current?.value) {
            eventNameRef.current.value = ""
        }
    }, [selectedEventType]);

    useEffect(() => {
        if (placeRef?.current?.value) {
            placeRef.current.value = ""
        }
    }, [selectedEventName, selectedEventType]);

    useEffect(() => {
        const { isFunerariumAddress } = getAddressConditions(selectedEventType, selectedEventName, selectedPlace);

        const currentType = eventTypeList?.find(e => String(e.id) === String(selectedEventType));
        const isRecueillement = selectedEventType === "Recueillement" || String(selectedEventType) === "2" || currentType?.name === "Recueillement";
        const isVisite = String(selectedEventName) === "1";

        if (isRecueillement && isVisite && isFunerariumAddress && funerariumData) {
            funerariumAutoFilledRef.current = true;
            setValues(prev => {
                const morningStart = normalizeTimeFormat(funerariumData.morningStart || funerariumData.time || prev?.morningStart);
                const morningEnd = normalizeTimeFormat(funerariumData.morningEnd || prev?.morningEnd);
                const afternoonStart = normalizeTimeFormat(funerariumData.afternoonHoursStart || funerariumData.afternoonStart || prev?.afternoonStart);
                const afternoonEnd = normalizeTimeFormat(funerariumData.afternoonHoursEnd || funerariumData.afternoonEnd || prev?.afternoonEnd);
                const visitInfos = funerariumData.visitInfos || prev?.visitInfos;

                if (prev?.morningStart === morningStart && prev?.afternoonStart === afternoonStart && prev?.visitInfos === visitInfos) return prev;

                return {
                    ...prev,
                    morningStart,
                    time: morningStart,
                    morningEnd,
                    afternoonStart,
                    afternoonEnd,
                    visitInfos
                };
            });
        } else if (isRecueillement && isVisite && funerariumAutoFilledRef.current) {
            // Le lieu a changé pour un funérarium hors agence (ou un autre lieu) après un
            // pré-remplissage automatique : ne pas conserver les horaires/infos de l'agence.
            funerariumAutoFilledRef.current = false;
            setValues(prev => ({
                ...prev,
                morningStart: "",
                time: "",
                morningEnd: "",
                afternoonStart: "",
                afternoonEnd: "",
                visitInfos: ""
            }));
        }
    }, [selectedEventType, selectedEventName, selectedPlace, funerariumData, eventTypeList]);

    useEffect(() => {

        const isFunerariumPlaceSelected = String(selectedPlace) === "10" || String(selectedPlace) === "9";

        if (isFunerariumPlaceSelected && funerariumData) {
            setValues(prev => {
                const addressData = funerariumData.address || {};

                const address = String(addressData.address || "");
                const addressComp = String(addressData.addressComp || "");
                const zipcode = String(addressData.zipcode || "");
                const city = String(addressData.city || "");
                const country = String(addressData.country || "FR");

                if (prev?.actualFunPlace?.address === address &&
                    prev?.actualFunPlace?.zipcode === zipcode &&
                    prev?.actualFunPlace?.city === city) {
                    return prev;
                }

                return {
                    ...prev,
                    actualFunPlace: {
                        address,
                        addressComp,
                        zipcode,
                        city,
                        country
                    }
                };
            });
        }
    }, [selectedEventType, selectedEventName, selectedPlace, funerariumData]);

    const onChangeEventName = (e) => {
        setSelectedEventName(eventNameRef?.current?.value)
        setSelectedPlace(null)
        validateFormInput(e)
    }

    const onChangePlace = (e) => {
        setSelectedPlace(placeRef?.current?.value)
        validateFormInput(e)
    }

    const onChange = (e) => {
        const { name, value } = e.target;

        if (name === "schedulePeriod") {
            setValues(prev => ({
                ...prev,
                schedulePeriod: value,
                ...(value === "afternoon" ? { morningStart: "", morningEnd: "" } : {}),
                ...(value === "morning" ? { afternoonStart: "", afternoonEnd: "" } : {}),
            }))
            return;
        }

        setValues(prev => ({
            ...prev,
            [name]: value
        }))
        validateFormInput(e)
    }

    const onChangeHideFromInternet = (e) => {
        setValues(prev => ({
            ...prev,
            hideFromInternet: e.target.checked
        }))
    }

    const onChangeAddress = (e) => {
        const { isFunerariumAddress } = getAddressConditions(selectedEventType, selectedEventName, selectedPlace);

        if (isFunerariumAddress) {
            setValues(prev => ({
                ...prev,
                actualFunPlace: {
                    ...prev?.actualFunPlace,
                    [e.target.name]: e.target.value
                },
            }))
        } else {
            setValues(prev => ({
                ...prev,
                actualPlace: {
                    country: "FR",
                    ...prev?.actualPlace,
                    [e.target.name]: e.target.value
                },
            }))
        }
        validateFormInput(e)
    }

    useEffect(() => {
        if (values?.isPublic == 1) {
            setPublished(1)
        }
    }, [values?.isPublic]);

    const validateTimeFormat = (timeValue) => {
        if (!timeValue) return true;
        const timeRegex = /^\d{2}:\d{2}$/;
        if (!timeRegex.test(timeValue)) return false;

        const [hours, minutes] = timeValue.split(':').map(Number);
        return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!checkRequiredFields()) {
            return;
        }

        let timeFields = [];

        if (selectedEventType === "Recueillement" && selectedEventName == 1) {
            timeFields = [];
            if (schedulePeriod !== "afternoon") {
                timeFields.push('morningStart', 'morningEnd');
            }
            if (schedulePeriod !== "morning") {
                timeFields.push('afternoonStart', 'afternoonEnd');
            }
        } else {
            timeFields = ['time'];
        }

        const invalidTimeFields = [];

        ['time', 'morningStart', 'morningEnd', 'afternoonStart', 'afternoonEnd'].forEach(field => {
            const input = document.querySelector(`input[name="${field}"]`);
            if (input && input.parentElement) {
                input.parentElement.classList.remove('invalid');
            }
        });

        timeFields.forEach(field => {
            const fieldValue = values?.[field];
            if (fieldValue && fieldValue.trim() !== "") {
                if (!validateTimeFormat(fieldValue)) {
                    invalidTimeFields.push(field);
                    const input = document.querySelector(`input[name="${field}"]`);
                    if (input && input.parentElement) {
                        input.parentElement.classList.add('invalid');
                        input.parentElement.classList.remove('valid');
                    }
                }
            }
        });

        if (invalidTimeFields.length > 0) {
            const errorMessage = "Les horaires doivent être au format HH:MM (ex: 09:00, 14:30)";
            const errorsDiv = document.querySelector('.errors');
            if (errorsDiv) {
                errorsDiv.innerHTML = `<p class="error">${errorMessage}</p>`;
            }
            return;
        }

        const { isDeceasedAddress, isFunerariumAddress } = getAddressConditions(selectedEventType, selectedEventName, selectedPlace);

        let addressEvent;
        if (isDeceasedAddress) {
            addressEvent = deceasedValues;
        } else if (isFunerariumAddress) {
            addressEvent = values?.actualFunPlace;
        } else {
            addressEvent = values?.actualPlace;
        }

        const agencyFunerarium = (isFunerariumAddress && funerariumData) ? {
            address: funerariumData.address || null,
            morningStart: normalizeTimeFormat(funerariumData.morningStart || funerariumData.time),
            morningEnd: normalizeTimeFormat(funerariumData.morningEnd),
            afternoonStart: normalizeTimeFormat(funerariumData.afternoonHoursStart || funerariumData.afternoonStart),
            afternoonEnd: normalizeTimeFormat(funerariumData.afternoonHoursEnd || funerariumData.afternoonEnd),
        } : null;

        const baseBodyData = {
            eventId: eventId,
            oldKey: eventInfos?.typeKey ? eventInfos?.typeKey : null,
            typeKey: selectedEventType,
            deceasedId: id,
            eventName: selectedEventName,
            eventPlace: selectedPlace,
            placeName: values?.placeName ? values?.placeName : null,
            accessCode: values?.accessCode ? values?.accessCode : null,
            addressEvent: addressEvent,
            agencyAddress: agencyContactInfos?.address || null,
            agencyHours: agencyMainInfos?.hours || null,
            agencyFunerarium: agencyFunerarium,
        };

        let body;
        if (selectedEventType === "Recueillement" && selectedEventName == 1) {
            body = {
                ...baseBodyData,
                isPublic: values?.isPublic ? values?.isPublic : 0,
                publish: values?.publish ? values?.publish : 0,
                hideFromInternet: requiredInput ? (values?.hideFromInternet ? true : false) : false,
                date: values?.date,
                time: values?.morningStart ? values?.morningStart : values?.time,
                endDate: values?.endDate,
                schedulePeriod: schedulePeriod,
                morningStart: schedulePeriod !== "afternoon" ? values?.morningStart : null,
                morningEnd: schedulePeriod !== "afternoon" ? values?.morningEnd : null,
                afternoonStart: schedulePeriod !== "morning" ? values?.afternoonStart : null,
                afternoonEnd: schedulePeriod !== "morning" ? values?.afternoonEnd : null,
                visitInfos: values?.visitInfos ? values?.visitInfos : null,
            }
        } else {
            body = {
                ...baseBodyData,
                publish: values?.publish ? values?.publish : 0,
                isPublic: values?.isPublic ? values?.isPublic : 0,
                hideFromInternet: requiredInput ? (values?.hideFromInternet ? true : false) : false,
                time: values?.time || null,
                date: values?.date || null,
                accessCode: values?.accessCode || null,
                endDate: null,
                morningStart: null,
                morningEnd: null,
                afternoonStart: null,
                afternoonEnd: null,
                visitInfos: null,
            }
        }

        await updateEventInfos(token, body)
            .then(response => {
                removeErrorList();
                if (!response?.response?.data?.error) {
                    setShowModal(false)
                    apiEventList()
                    apiDeceasedInfos()
                } else {
                    getErrorListFromValidation(response?.response?.data?.error)
                }
            })
    }

    const renderAddressSection = () => {
        const { isDeceasedAddress, isFunerariumAddress, shouldShowAddressForm } =
            getAddressConditions(selectedEventType, selectedEventName, selectedPlace);

        if (isDeceasedAddress) {
            return (
                <fieldset>
                    <legend>Informations sur l'adresse</legend>
                    <InputFullAddress
                        zipField="zipcode"
                        cityField="city"
                        addressField="address"
                        compField="addressComp"
                        countryField="country"
                        address={deceasedValues?.address}
                        comp={deceasedValues?.addressComp}
                        zipcode={deceasedValues?.zipcode}
                        city={deceasedValues?.city}
                        country={deceasedValues?.country}
                        onChange={onChangeAddress}
                        required={true}
                        readonly={true}
                        event={true}
                        disabled
                        type="actual"
                    />
                </fieldset>
            );
        }

        if (shouldShowAddressForm) {
            return (
                <fieldset>
                    <legend>Informations sur l'adresse</legend>
                    <InputFullAddress
                        addressField="address"
                        compField="addressComp"
                        zipField="zipcode"
                        cityField="city"
                        countryField="country"
                        onChange={onChangeAddress}
                        readonly={isFunerariumAddress}
                        address={isFunerariumAddress ? values?.actualFunPlace?.address : values?.actualPlace?.address}
                        comp={isFunerariumAddress ? values?.actualFunPlace?.addressComp : values?.actualPlace?.addressComp}
                        zipcode={isFunerariumAddress ? values?.actualFunPlace?.zipcode : values?.actualPlace?.zipcode}
                        city={isFunerariumAddress ? values?.actualFunPlace?.city : values?.actualPlace?.city}
                        country={isFunerariumAddress ? values?.actualFunPlace?.country : values?.actualPlace?.country || "FR"}
                        compClass="address_comp_none"
                        required={true}
                        event={true}
                        type="actual"
                        disabled={isFunerariumAddress}
                    />
                </fieldset>
            );
        }

        return null;
    };

    const flowerPlaces = flowerLocations?.flowerPlaces || [];
    const { isFunerariumAddress } = getAddressConditions(selectedEventType, selectedEventName, selectedPlace);
    // Le code d'accès n'a de sens que si le lieu est le domicile du défunt (6) ou un funérarium (9, 10), quel que soit le type d'événement.
    const showAccessCode = selectedPlace == 6 || selectedPlace == 9 || selectedPlace == 10;
    const requiredInput = (selectedEventType === "Rassemblement" && flowerPlaces.includes("flowerRassemblement")) ||
        (selectedEventType === "Recueillement" && flowerPlaces.includes("flowerRecueillement")) ||
        (isFunerariumAddress && flowerPlaces.includes("flowerFunerarium"));
    const schedulePeriod = values?.schedulePeriod || "both";
    const morningRequired = schedulePeriod !== "afternoon";
    const afternoonRequired = schedulePeriod !== "morning";

    return (
        <form onSubmit={handleSubmit} method="POST" noValidate>
            <Icon span="Détail de l'événement" source="fa fa-calendar"/>
            <fieldset>
                <div className="form-input">
                    <label htmlFor="typeKey" className="required">Choix de l'événement</label>
                    <select
                        name="typeKey"
                        ref={eventTypeRef}
                        required
                        onChange={onChangeMainEvent}
                    >
                        <option name="typeKey" disabled={true} value="" selected={!eventId && true} >Choisissez un événement</option>
                        {eventTypeList?.map((event, index) => (
                            <option key={index} selected={eventId && event?.id == values?.typeKey} name="typeKey" value={event?.id}>
                                {event?.name}
                            </option>
                        ))}
                    </select>
                </div>
                {(selectedEventType && selectedEventType !== "Ceremonie" && selectedEventType !== "Obseques") &&
                    <div className="form-input">
                        <label htmlFor="eventName" className="required">Nom de l'événement</label>
                        <select name="eventName" ref={eventNameRef} onChange={onChangeEventName} required>
                            <option disabled={true} selected={!eventId && true} name="eventName" value="">Préciser</option>
                            {selectedEventSubList &&
                                selectedEventSubList?.filter(subList => subList != null)?.map((event, index) => (
                                    <option selected={eventId && event?.id == values?.eventType} name="eventName" key={index} value={event?.id}>
                                        {event?.name}
                                    </option>
                                ))
                            }
                        </select>
                    </div>
                }
                {(selectedEventType && ((selectedEventName && selectedEventName != "7") || selectedEventType === "Ceremonie" || selectedEventType === "Obseques")) &&
                    <div className="form-input">
                        <label htmlFor="eventPlace" className="required">Type de lieu</label>
                        <select name="eventPlace" ref={placeRef} onChange={onChangePlace} required>
                            <option disabled={true} name="eventPlace" value="" selected={true}>Préciser</option>
                            {(selectedEventType === "Ceremonie" || selectedEventType === "Obseques" ? selectedPlaceList : commonCremationSubListPlaces) &&
                                (selectedEventType === "Ceremonie" || selectedEventType === "Obseques" ? selectedPlaceList : commonCremationSubListPlaces)?.filter(placeList => placeList != null)?.map((place, index) => (
                                    <option selected={eventId && place?.id == values?.placeKey} name="eventPlace" key={index} value={place?.id}>
                                        {place?.name}
                                    </option>
                                ))
                            }
                        </select>
                    </div>
                }
                {(selectedEventType && selectedEventName == "7") &&
                    <div className="form-input">
                        <label htmlFor="eventPlace" className="required">Type de lieu</label>
                        <select name="eventPlace" ref={placeRef} onChange={onChangePlace} required>
                            <option disabled={true} name="eventPlace" value="" selected={true}>Préciser</option>
                            {departCremationSubListPlaces &&
                                departCremationSubListPlaces?.filter(placeList => placeList != null)?.map((place, index) => (
                                    <option selected={eventId && place?.id == values?.placeKey} name="eventPlace" key={index} value={place?.id}>
                                        {place?.name}
                                    </option>
                                ))
                            }
                        </select>
                    </div>
                }
                {!selectedPlace == 0 && selectedEventType && (selectedEventName || selectedEventType === "Ceremonie" || selectedEventType === "Obseques") &&
                    <div className="form-input">
                        <label htmlFor="placeName">Nom du lieu</label>
                        {values?.placeKey != 0 &&
                            <input defaultValue={eventId && values?.placeOther ? values?.placeOther : values?.placeName} type="text" name="placeName" onChange={onChange} />
                        }
                    </div>
                }
            </fieldset>
            {renderAddressSection()}
            {selectedEventType === "Recueillement" && selectedEventName == 1 && selectedPlace && (
                <fieldset>
                        <div className="dates-schedule-row">
                            <div className="form-group dates-column">
                                <h4 className="schedule-subtitle">DATES</h4>
                                <div className="form-input flex">
                                    <label htmlFor="date" className="required">
                                        Début visites
                                    </label>
                                    <DateInput
                                        value={values?.date || ""}
                                        max="9999-12-31"
                                        name="date"
                                        onChange={onChange}
                                        required
                                    />
                                </div>
                                <div className="form-input flex">
                                    <label htmlFor="endDate" className="required">
                                        Fin visites
                                    </label>
                                    <DateInput
                                        value={values?.endDate || ""}
                                        max="9999-12-31"
                                        name="endDate"
                                        onChange={onChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="schedule-column">
                                <h4 className="schedule-subtitle">Horaires</h4>
                                <div className="form-input flex">
                                    <label htmlFor="schedulePeriod" className="required">
                                        Créneau
                                    </label>
                                    <select
                                        name="schedulePeriod"
                                        value={schedulePeriod}
                                        onChange={onChange}
                                        required
                                    >
                                        <option value="morning">Matin</option>
                                        <option value="afternoon">Après-midi</option>
                                        <option value="both">Les deux</option>
                                    </select>
                                </div>
                                <div className="schedule-grid">
                                    <div className="schedule-grid-cell header"></div>
                                    <div className="schedule-grid-cell header">
                                        <span>Ouverture</span>
                                    </div>
                                    <div className="schedule-grid-cell header">
                                        <span>Fermeture</span>
                                    </div>

                                    <div className="schedule-grid-cell label">
                                        <label className={morningRequired ? "required" : ""}>Matin</label>
                                    </div>
                                    <div className="form-input schedule-grid-cell">
                                        <input
                                            value={values?.morningStart || ""}
                                            type="time"
                                            name="morningStart"
                                            onChange={onChange}
                                            disabled={!morningRequired}
                                            required={morningRequired}
                                        />
                                    </div>
                                    <div className="form-input schedule-grid-cell">
                                        <input
                                            value={values?.morningEnd || ""}
                                            type="time"
                                            name="morningEnd"
                                            onChange={onChange}
                                            disabled={!morningRequired}
                                            required={morningRequired}
                                        />
                                    </div>

                                    <div className="schedule-grid-cell label">
                                        <label className={afternoonRequired ? "required" : ""}>Après-midi</label>
                                    </div>
                                    <div className="form-input schedule-grid-cell">
                                        <input
                                            value={values?.afternoonStart || ""}
                                            type="time"
                                            name="afternoonStart"
                                            onChange={onChange}
                                            disabled={!afternoonRequired}
                                            required={afternoonRequired}
                                        />
                                    </div>
                                    <div className="form-input schedule-grid-cell">
                                        <input
                                            value={values?.afternoonEnd || ""}
                                            type="time"
                                            name="afternoonEnd"
                                            onChange={onChange}
                                            disabled={!afternoonRequired}
                                            required={afternoonRequired}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    <div className="form-input visit-info-textarea">
                            <label htmlFor="visitInfos">
                                <i className="fa fa-info-circle"></i>
                                Informations de visite
                            </label>
                            <textarea
                                value={values?.visitInfos || ""}
                                name="visitInfos"
                                onChange={onChange}
                                rows="4"
                                placeholder="Informations complémentaires sur les visites..."
                            />
                        </div>
                        {showAccessCode &&
                            <div className="form-input">
                                <label htmlFor="accessCode">Code d'accès</label>
                                <input defaultValue={values?.accessCode} type="text" name="accessCode" onChange={onChange} />
                            </div>
                        }
                        {requiredInput &&
                            <div className="form-input">
                                <label htmlFor="hideFromInternet">Ne pas afficher sur Internet</label>
                                <input
                                    type="checkbox"
                                    id="hideFromInternet"
                                    name="hideFromInternet"
                                    checked={values?.hideFromInternet || false}
                                    onChange={onChangeHideFromInternet}
                                />
                            </div>
                        }
                    </fieldset>
                )}
            {selectedEventType && selectedEventName && selectedPlace && !(selectedEventType === "Recueillement" && selectedEventName == 1) && selectedEventType !== "Ceremonie" && selectedEventType !== "Obseques" && (
                <fieldset>
                    <legend>Horaires</legend>
                    <div className="simple-schedule-container flex">
                        <div className="form-input">
                            <label htmlFor="date" className="required">
                                <i className="fa fa-calendar"></i>
                                Date
                            </label>
                            <DateInput
                                defaultValue={eventId && values?.date}
                                min={today}
                                max="9999-12-31"
                                name="date"
                                onChange={onChange}
                                required
                            />
                        </div>
                        <div className="form-input column">
                            <label htmlFor="time" className="required">
                                <i className="fa fa-clock"></i>
                                Heure
                            </label>
                            <input
                                value={values?.time || ""}
                                type="time"
                                name="time"
                                onChange={onChange}
                                required
                            />
                        </div>
                    </div>
                    {showAccessCode &&
                        <div className="form-input">
                            <label htmlFor="accessCode">Code d'accès</label>
                            <input defaultValue={values?.accessCode} type="text" name="accessCode" onChange={onChange} />
                        </div>
                    }
                    {requiredInput &&
                        <div className="form-input">
                            <label htmlFor="hideFromInternet">Ne pas afficher sur Internet</label>
                            <input
                                type="checkbox"
                                id="hideFromInternet"
                                name="hideFromInternet"
                                checked={values?.hideFromInternet || false}
                                onChange={onChangeHideFromInternet}
                            />
                        </div>
                    }
                </fieldset>
            )}
            {((selectedEventType === "Ceremonie" || selectedEventType === "Obseques") && selectedPlace != null) && (
                <fieldset>
                    <legend>Horaires</legend>
                    <div className="simple-schedule-container flex">
                        <div className="form-input">
                            <label htmlFor="date" className="required">
                                <i className="fa fa-calendar"></i>
                                Date
                            </label>
                            <DateInput
                                defaultValue={eventId && values?.date}
                                min={today}
                                max="9999-12-31"
                                name="date"
                                onChange={onChange}
                                required
                            />
                        </div>
                        <div className="form-input column">
                            <label htmlFor="time" className="required">
                                <i className="fa fa-clock"></i>
                                Heure
                            </label>
                            <input
                                value={values?.time || ""}
                                type="time"
                                name="time"
                                onChange={onChange}
                                required
                            />
                        </div>
                    </div>
                    {showAccessCode &&
                        <div className="form-input">
                            <label htmlFor="accessCode">Code d'accès</label>
                            <input defaultValue={values?.accessCode} type="text" name="accessCode" onChange={onChange} />
                        </div>
                    }
                </fieldset>
            )}
            {((selectedEventType && selectedEventName && selectedPlace) || ((selectedEventType === "Ceremonie" || selectedEventType === "Obseques") && selectedPlace != null)) &&
                <>
                    <fieldset className="column">
                        <legend>Autorisations</legend>
                        <div className="form-input">
                            <label htmlFor="isPublic" className="required">La famille souhaite-t-elle rester dans l'intimité familiale ?</label>
                            <input type="radio" name="isPublic" id="isPublic0" value={0} checked={values?.isPublic == 0} onChange={onChange} required /><label htmlFor="isPublic0">Oui</label>
                            <input type="radio" name="isPublic" id="isPublic1" value={1} checked={values?.isPublic == 1} onChange={onChange} /><label htmlFor="isPublic1">Non</label>
                        </div>
                        {(values?.isPublic == 0) &&
                            <div className="form-input">
                                <label htmlFor="publish" className="required">Autoriser la publication sur Internet ?</label>
                                <input type="radio" name="publish" id="publish1" value={1} checked={values?.publish == 1} onChange={onChange} required /><label htmlFor="publish1">Oui</label>
                                <input type="radio" name="publish" id="publish0" value={0} checked={values?.publish == 0} onChange={onChange} /><label htmlFor="publish0">Non</label>
                            </div>
                        }
                        <div className="errors"></div>
                    </fieldset>
                </>
            }
            <FormButtons handleRemoveModal={handleRemoveModal} />
        </form>
    )
}
